import { AudioLines, Plus } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import type { ReactNode } from "react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { greetingFor } from "@/lib/greeting"
import { cn } from "@/lib/utils"
import { COACH_FALLBACK_LABEL, initialsOf, type CoachStyle } from "@nl/shared"

/**
 * Critically damped: the box travels from the stage to the bottom of the
 * screen, and an overshoot there would read as the input bouncing off the
 * edge. Shared by everything that moves between the two states of the start
 * page, so the identity and the composer arrive together.
 */
export const STAGE_SPRING = {
  type: "spring",
  bounce: 0,
  duration: 0.55,
} as const

/**
 * Portrait and name of the coach. One `layoutId` across both sizes, so the
 * large identity in the box and the small one in the conversation header are
 * the same thing moving, not two things swapping.
 */
export function CoachIdentity({
  coach,
  size,
}: {
  coach: CoachStyle | null
  size: "lg" | "sm"
}) {
  const name = coach?.name ?? COACH_FALLBACK_LABEL
  return (
    <motion.div
      layoutId="coach-identity"
      transition={STAGE_SPRING}
      className={cn("flex items-center", size === "lg" ? "gap-3.5" : "gap-2.5")}
    >
      <Avatar
        className={cn(
          "shrink-0 ring-1 ring-[var(--rule)]",
          size === "lg" ? "size-14" : "size-8"
        )}
      >
        {coach?.portraitUrl ? <AvatarImage src={coach.portraitUrl} alt="" /> : null}
        <AvatarFallback
          className={cn(
            "bg-accent font-medium text-primary",
            size === "lg" ? "text-[16px]" : "text-[11px]"
          )}
        >
          {coach ? initialsOf(coach.name) : ""}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 leading-tight">
        <div
          className={cn(
            "truncate font-medium",
            size === "lg" ? "text-[15px]" : "text-[14px]"
          )}
        >
          {name}
        </div>
        <div
          className={cn(
            "truncate text-muted-foreground",
            size === "lg" ? "text-[13px]" : "text-[12px]"
          )}
        >
          Dein KI-Coach
        </div>
      </div>
    </motion.div>
  )
}

/**
 * The start page before the first message: the artwork as the stage, the
 * coach in one closed box on it, and the two ways of talking to him side by
 * side. The composer comes in from the thread so that the very same input
 * later sits at the bottom of the conversation.
 */
export function CoachStage({
  coach,
  firstName,
  standHint,
  composer,
  onSpeak,
  voice,
}: {
  coach: CoachStyle | null
  firstName?: string
  /** Where the advisor stands, as one spoken sentence. */
  standHint?: string
  composer: ReactNode
  onSpeak: () => void
  /** The running call. While it is on, it *is* the box. */
  voice?: ReactNode | null
}) {
  const name = coach?.name ?? COACH_FALLBACK_LABEL
  return (
    <section className="relative w-full overflow-hidden bg-[var(--surface-alt)]">
      <img
        src="/neueleben-artwork.png"
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover dark:opacity-70"
      />
      {/* Light from above, page colour below: the artwork stays a stage and
          runs out into the page instead of ending on an edge.

          FALLE: der untere Stopp muss VOR dem Rand liegen. Stand er auf 100 %,
          war das Bild in der letzten Pixelzeile noch schwach zu sehen und die
          Kante damit sichtbar, obwohl Bühne und Seite dieselbe Farbe haben. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-[linear-gradient(to_bottom,color-mix(in_oklab,var(--paper)_20%,transparent)_0%,transparent_30%,var(--paper)_86%)]"
      />

      <div className="relative z-10 mx-auto flex min-h-[480px] w-full max-w-[1100px] items-center justify-center px-8 py-14">
        <motion.div
          layoutId="coach-card"
          transition={STAGE_SPRING}
          className={cn(
            "w-full max-w-[720px] rounded-2xl p-6 sm:p-8",
            // Glassy on purpose: the artwork stays readable through the box
            // instead of being covered by an opaque slab.
            "bg-[var(--paper)]/72 shadow-[0_24px_80px_rgba(31,31,31,0.16)] ring-1 ring-[var(--paper)]/80 backdrop-blur-2xl"
          )}
        >
          <AnimatePresence mode="wait" initial={false}>
            {voice ? (
              <motion.div
                key="voice"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                // The panel brings its own padding and a footer bar; pulled to
                // the box edges so that bar closes the box instead of floating
                // inside it.
                className="-m-6 sm:-m-8 [&>div:last-child]:rounded-b-2xl"
              >
                {voice}
              </motion.div>
            ) : (
              <motion.div
                key="prompt"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
              <CoachIdentity coach={coach} size="lg" />

              <p className="mt-6 font-display text-[26px] leading-[1.15] font-medium tracking-tight sm:text-[30px]">
                {greetingFor(new Date())}
                {firstName ? `, ${firstName}` : ""}.
                <br />
                <span className="text-foreground/65">
                  Woran möchtest du heute arbeiten?
                </span>
              </p>
              {standHint ? (
                <p className="mt-2.5 text-[13.5px] text-muted-foreground">
                  {standHint}
                </p>
              ) : null}

              <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:items-stretch">
                <motion.div
                  layoutId="composer"
                  transition={STAGE_SPRING}
                  className="min-w-0 flex-1"
                >
                  {composer}
                </motion.div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onSpeak}
                  className="h-auto min-h-11 shrink-0 gap-2 self-stretch bg-[var(--paper)]/85 px-4 text-[14px]"
                >
                  <AudioLines className="h-4 w-4 text-primary" />
                  Mit {name} sprechen
                </Button>
              </div>
              <p className="mt-3 text-[12.5px] text-muted-foreground">
                Schreib mir oder sprich live mit mir, wie am Telefon. Das Gespräch
                landet danach hier im Verlauf.
              </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  )
}

/** The slim header once the conversation runs; the identity lands here. */
export function ConversationHeader({
  coach,
  onNew,
}: {
  coach: CoachStyle | null
  onNew: () => void
}) {
  return (
    <header className="shrink-0 border-b border-[var(--rule)] bg-[var(--paper)]/85 backdrop-blur-xl">
      <div className="mx-auto flex w-full max-w-[880px] items-center gap-3 px-8 py-3">
        <CoachIdentity coach={coach} size="sm" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onNew}
          className="ml-auto gap-1.5 text-muted-foreground hover:text-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
          Neues Gespräch
        </Button>
      </div>
    </header>
  )
}
